import React from "react";
import { View, StyleSheet, Pressable } from "react-native";
import { ThemedText } from "@/components/ThemedText";
import ThemedInput from "@/components/ThemedInput";
import { ToggleSwitch } from "@/components/ui/ToggleSwitch";
import { useTheme } from "@/hooks/useTheme";
import type { CourseMetaType } from "@/types/db/courses";

export interface CourseSettingsCardProps {
  metaData: CourseMetaType;
  onUpdate: (updates: Partial<CourseMetaType>) => void;
}

export function CourseSettingsCard({ metaData, onUpdate }: CourseSettingsCardProps) {
  const { colors } = useTheme();

  const updateSettings = (updates: Partial<CourseMetaType["settings"]>) => {
    onUpdate({
      settings: {
        ...metaData.settings,
        ...updates,
      },
    });
  };

  const updateGrading = (updates: Partial<CourseMetaType["grading"]>) => {
    onUpdate({
      grading: {
        ...metaData.grading,
        ...updates,
      },
    });
  };

  const settings = metaData.settings || {};
  const grading = metaData.grading || {};

  return (
    <View style={[styles.card, { backgroundColor: colors.card, borderColor: colors.border }]}>
      <ThemedText type="subtitle">Course Settings</ThemedText>

      {/* Grading Method */}
      <View style={styles.section}>
        <ThemedText style={styles.label}>Grading Method</ThemedText>
        <View style={styles.toggleGroup}>
          {["percentage", "points", "weighted"].map((method) => (
            <Pressable
              key={method}
              style={[
                styles.toggleButton,
                {
                  backgroundColor: grading.method === method ? colors.primary : colors.backgroundSecondary,
                  borderColor: grading.method === method ? colors.primary : colors.border,
                },
              ]}
              onPress={() => updateGrading({ method: method as any })}
            >
              <ThemedText
                style={{
                  color: grading.method === method ? "white" : colors.text,
                  fontSize: 13,
                  textTransform: "capitalize",
                }}
              >
                {method}
              </ThemedText>
            </Pressable>
          ))}
        </View>
      </View>

      {/* Passing Score */}
      <View style={styles.section}>
        <ThemedInput
          label="Passing Score (%)"
          value={String(grading.passingScore || 70)}
          onChangeText={(text) => updateGrading({ passingScore: parseInt(text) || 70 })}
          keyboardType="numeric"
          placeholder="70"
        />
      </View>

      {/* Max Attempts */}
      <View style={styles.section}>
        <ThemedText style={styles.label}>Max Attempts</ThemedText>
        <View style={styles.row}>
          <ThemedInput
            value={settings.maxAttempts === null ? "" : String(settings.maxAttempts || 3)}
            onChangeText={(text) => updateSettings({ maxAttempts: text === "" ? null : parseInt(text) || 3 })}
            keyboardType="numeric"
            placeholder="Unlimited"
            style={{ flex: 1 }}
          />
          <View style={styles.toggleRow}>
            <ThemedText style={styles.helperText}>Unlimited</ThemedText>
            <ToggleSwitch
              value={settings.maxAttempts === null}
              onValueChange={(val) => updateSettings({ maxAttempts: val ? null : 3 })}
            />
          </View>
        </View>
      </View>

      {/* Time Limit */}
      <View style={styles.section}>
        <ThemedText style={styles.label}>Time Limit (minutes)</ThemedText>
        <View style={styles.row}>
          <ThemedInput
            value={settings.timeLimit ? String(settings.timeLimit) : ""}
            onChangeText={(text) => updateSettings({ timeLimit: text === "" ? null : parseInt(text) || null })}
            keyboardType="numeric"
            placeholder="No limit"
            style={{ flex: 1 }}
          />
          <View style={styles.toggleRow}>
            <ThemedText style={styles.helperText}>No limit</ThemedText>
            <ToggleSwitch
              value={!settings.timeLimit}
              onValueChange={(val) => updateSettings({ timeLimit: val ? null : 30 })}
            />
          </View>
        </View>
      </View>

      {/* Allow Retry */}
      <View style={styles.toggleRow}>
        <View style={{ flex: 1 }}>
          <ThemedText style={styles.label}>Allow Retry</ThemedText>
          <ThemedText style={styles.helperText}>Allow students to retake the course</ThemedText>
        </View>
        <ToggleSwitch
          value={settings.allowRetry !== false}
          onValueChange={(val) => updateSettings({ allowRetry: val })}
        />
      </View>

      {/* Certificate Enabled */}
      <View style={styles.toggleRow}>
        <View style={{ flex: 1 }}>
          <ThemedText style={styles.label}>Certificate Enabled</ThemedText>
          <ThemedText style={styles.helperText}>Generate certificate on completion</ThemedText>
        </View>
        <ToggleSwitch
          value={settings.certificateEnabled === true}
          onValueChange={(val) => updateSettings({ certificateEnabled: val })}
        />
      </View>

      {/* Show Correct Answers */}
      <View style={styles.toggleRow}>
        <View style={{ flex: 1 }}>
          <ThemedText style={styles.label}>Show Correct Answers</ThemedText>
          <ThemedText style={styles.helperText}>Display correct answers to students</ThemedText>
        </View>
        <ToggleSwitch
          value={settings.showCorrectAnswers === true}
          onValueChange={(val) => updateSettings({ showCorrectAnswers: val })}
        />
      </View>

      {/* When to show answers */}
      {settings.showCorrectAnswers && (
        <View style={styles.section}>
          <ThemedText style={styles.label}>Show Answers After</ThemedText>
          <View style={styles.toggleGroup}>
            {["immediate", "completion", "passing"].map((timing) => (
              <Pressable
                key={timing}
                style={[
                  styles.toggleButton,
                  {
                    backgroundColor:
                      settings.showCorrectAnswersAfter === timing ? colors.primary : colors.backgroundSecondary,
                    borderColor: settings.showCorrectAnswersAfter === timing ? colors.primary : colors.border,
                  },
                ]}
                onPress={() => updateSettings({ showCorrectAnswersAfter: timing as any })}
              >
                <ThemedText
                  style={{
                    color: settings.showCorrectAnswersAfter === timing ? "white" : colors.text,
                    fontSize: 13,
                    textTransform: "capitalize",
                  }}
                >
                  {timing}
                </ThemedText>
              </Pressable>
            ))}
          </View>
        </View>
      )}

      {/* Shuffle Questions */}
      <View style={styles.toggleRow}>
        <View style={{ flex: 1 }}>
          <ThemedText style={styles.label}>Shuffle Questions</ThemedText>
          <ThemedText style={styles.helperText}>Randomize question order</ThemedText>
        </View>
        <ToggleSwitch
          value={settings.shuffleQuestions === true}
          onValueChange={(val) => updateSettings({ shuffleQuestions: val })}
        />
      </View>

      {/* Shuffle Options */}
      <View style={styles.toggleRow}>
        <View style={{ flex: 1 }}>
          <ThemedText style={styles.label}>Shuffle Options</ThemedText>
          <ThemedText style={styles.helperText}>Randomize answer options</ThemedText>
        </View>
        <ToggleSwitch
          value={settings.shuffleOptions === true}
          onValueChange={(val) => updateSettings({ shuffleOptions: val })}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  card: {
    padding: 16,
    borderRadius: 12,
    borderWidth: 1,
    gap: 16,
  },
  section: {
    gap: 8,
  },
  label: {
    fontSize: 14,
    fontWeight: "600",
    marginBottom: 4,
  },
  helperText: {
    fontSize: 12,
    opacity: 0.7,
  },
  row: {
    flexDirection: "row",
    gap: 12,
    alignItems: "center",
  },
  toggleRow: {
    flexDirection: "row",
    alignItems: "center",
    gap: 12,
    paddingVertical: 8,
  },
  toggleGroup: {
    flexDirection: "row",
    gap: 8,
    flexWrap: "wrap",
  },
  toggleButton: {
    paddingVertical: 10,
    paddingHorizontal: 16,
    borderRadius: 8,
    borderWidth: 1,
    alignItems: "center",
    justifyContent: "center",
  },
});
